import React, { useState, useEffect } from 'react'
import { User, UserFilters as UserFiltersType, UserFormData, HourlyRateFormData, UserLevelFormData, BankInfoFormData, PasswordResetFormData } from '../../types'
import { userService } from '../../services/userService'
import UserFilters from './components/UserFilters'
import UserCard from './components/UserCard'
import UserForm from './components/UserForm'
import HourlyRateModal from './components/HourlyRateModal'
import UserLevelModal from './components/UserLevelModal'
import BankInfoModal from './components/BankInfoModal'
import PasswordResetModal from './components/PasswordResetModal'
import toast from 'react-hot-toast'

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [formLoading, setFormLoading] = useState(false)
  const [filters, setFilters] = useState<UserFiltersType>({
    isActive: true,
    search: ''
  })

  // Modal states
  const [showUserForm, setShowUserForm] = useState(false)
  const [showHourlyRateModal, setShowHourlyRateModal] = useState(false)
  const [showUserLevelModal, setShowUserLevelModal] = useState(false)
  const [showBankInfoModal, setShowBankInfoModal] = useState(false)
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false)

  // Selected user for modals
  const [editingUser, setEditingUser] = useState<User | undefined>()
  const [selectedUser, setSelectedUser] = useState<User | undefined>()

  // 載入用戶列表
  const loadUsers = async () => {
    try {
      setLoading(true)
      const usersData = await userService.getUsers(filters)
      setUsers(usersData)
    } catch (error) {
      console.error('Failed to load users:', error)
      toast.error('載入用戶列表失敗')
    } finally {
      setLoading(false)
    }
  }

  // 初始載入
  useEffect(() => {
    loadUsers()
  }, [])

  // 當篩選條件改變時重新載入
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadUsers()
    }, 300) // 防抖

    return () => clearTimeout(timeoutId)
  }, [filters])

  // 處理建立/編輯用戶
  const handleSubmitUser = async (data: UserFormData) => {
    try {
      setFormLoading(true)
      
      if (editingUser) {
        // 更新用戶
        await userService.updateUser(editingUser.id, data)
        toast.success('用戶更新成功')
      } else {
        // 建立新用戶
        await userService.createUser(data)
        toast.success('用戶建立成功')
      }
      
      setShowUserForm(false)
      setEditingUser(undefined)
      loadUsers()
    } catch (error: any) {
      console.error('Failed to submit user:', error)
      
      // 檢查是否為 email 重複錯誤
      const errorMessage = error.message || ''
      if (errorMessage.includes('Email already exists') || 
          errorMessage.includes('email already exists') ||
          errorMessage.includes('User with this email already exists')) {
        toast.error('該 Email 地址已被使用，請使用其他 Email')
      } else if (errorMessage.includes('Invalid token') || errorMessage.includes('Access token required')) {
        toast.error('登入已過期，請重新登入')
      } else if (errorMessage.includes('Missing required fields')) {
        toast.error('請填寫所有必填欄位')
      } else {
        // 顯示通用錯誤訊息
        toast.error(editingUser ? '用戶更新失敗' : '用戶建立失敗')
      }
    } finally {
      setFormLoading(false)
    }
  }

  // 處理編輯用戶
  const handleEditUser = (user: User) => {
    setEditingUser(user)
    setShowUserForm(true)
  }

  // 處理建立新用戶
  const handleCreateUser = () => {
    setEditingUser(undefined)
    setShowUserForm(true)
  }

  // 處理取消表單
  const handleCancelForm = () => {
    setShowUserForm(false)
    setEditingUser(undefined)
  }

  // 處理設定時薪
  const handleSetHourlyRate = (user: User) => {
    setSelectedUser(user)
    setShowHourlyRateModal(true)
  }

  // 處理提交時薪
  const handleSubmitHourlyRate = async (data: HourlyRateFormData) => {
    if (!selectedUser) return

    try {
      setFormLoading(true)
      await userService.updateHourlyRate(selectedUser.id, data)
      toast.success('時薪更新成功')
      setShowHourlyRateModal(false)
      setSelectedUser(undefined)
      loadUsers()
    } catch (error) {
      console.error('Failed to update hourly rate:', error)
      toast.error('時薪更新失敗')
    } finally {
      setFormLoading(false)
    }
  }

  // 處理設定職等
  const handleSetLevel = (user: User) => {
    setSelectedUser(user)
    setShowUserLevelModal(true)
  }

  // 處理提交職等
  const handleSubmitLevel = async (data: UserLevelFormData) => {
    if (!selectedUser) return

    try {
      setFormLoading(true)
      await userService.updateUserLevel(selectedUser.id, data)
      toast.success('職等調整成功')
      setShowUserLevelModal(false)
      setSelectedUser(undefined)
      loadUsers()
    } catch (error: any) {
      console.error('Failed to update user level:', error)
      
      // 建立詳細的錯誤訊息
      let errorMessage = '職等調整失敗'
      
      if (error.status) {
        errorMessage += ` (HTTP ${error.status})`
      }
      
      if (error.context) {
        errorMessage += ` - ${error.context}`
      }
      
      // 根據不同的錯誤狀態碼提供更具體的訊息
      if (error.status === 401) {
        errorMessage = '登入已過期，請重新登入'
      } else if (error.status === 403) {
        errorMessage = '權限不足，無法調整職等'
      } else if (error.status === 404) {
        errorMessage = '找不到指定的用戶'
      } else if (error.status === 400) {
        // 如果後端回傳了具體的錯誤訊息，優先使用
        if (error.errorData && error.errorData.message) {
          errorMessage = `職等調整失敗：${error.errorData.message}`
        } else {
          errorMessage = '職等調整失敗：請求參數錯誤'
        }
      } else if (error.status >= 500) {
        errorMessage = `職等調整失敗：伺服器錯誤 (${error.status})`
        
        // 如果有原始回應，也記錄下來
        if (error.rawResponse) {
          console.error('Server error response:', error.rawResponse)
        }
      } else if (error.message && !error.message.includes('HTTP')) {
        // 如果有具體的錯誤訊息且不是 HTTP 錯誤，使用它
        errorMessage = `職等調整失敗：${error.message}`
      }
      
      // 添加調試資訊到 console（只在開發環境）
      if (import.meta.env.DEV) {
        console.group('職等調整錯誤詳情')
        console.log('錯誤物件:', error)
        console.log('HTTP 狀態:', error.status)
        console.log('回應資料:', error.errorData)
        console.log('原始回應:', error.rawResponse)
        console.log('請求 URL:', error.url)
        console.groupEnd()
      }
      
      toast.error(errorMessage)
    } finally {
      setFormLoading(false)
    }
  }

  // 處理管理銀行資訊
  const handleManageBankInfo = (user: User) => {
    setSelectedUser(user)
    setShowBankInfoModal(true)
  }

  // 處理提交銀行資訊
  const handleSubmitBankInfo = async (data: BankInfoFormData) => {
    if (!selectedUser) return

    try {
      setFormLoading(true)
      await userService.updateBankInfo(selectedUser.id, data)
      toast.success('銀行資訊更新成功')
      setShowBankInfoModal(false)
      setSelectedUser(undefined)
      loadUsers()
    } catch (error) {
      console.error('Failed to update bank info:', error)
      toast.error('銀行資訊更新失敗')
    } finally {
      setFormLoading(false)
    }
  }

  // 處理重設密碼
  const handleResetPassword = (user: User) => {
    setSelectedUser(user)
    setShowPasswordResetModal(true)
  }

  // 處理提交密碼重設
  const handleSubmitPasswordReset = async (data: PasswordResetFormData) => {
    if (!selectedUser) return

    try {
      setFormLoading(true)
      await userService.resetPassword(selectedUser.id, data)
      toast.success('密碼重設成功')
      setShowPasswordResetModal(false)
      setSelectedUser(undefined)
    } catch (error) {
      console.error('Failed to reset password:', error)
      toast.error('密碼重設失敗')
    } finally {
      setFormLoading(false)
    }
  }

  // 處理用戶狀態變更
  const handleToggleUserStatus = async (userId: string, isActive: boolean) => {
    try {
      await userService.updateUserStatus(userId, isActive)
      toast.success(`用戶已${isActive ? '啟用' : '停用'}`)
      loadUsers()
    } catch (error) {
      console.error('Failed to update user status:', error)
      toast.error('用戶狀態更新失敗')
    }
  }

  // 處理取消 Modal
  const handleCancelModal = () => {
    setShowHourlyRateModal(false)
    setShowUserLevelModal(false)
    setShowBankInfoModal(false)
    setShowPasswordResetModal(false)
    setSelectedUser(undefined)
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div>
        <h1 className="text-3xl font-bold text-secondary-800">成員管理</h1>
        <p className="text-secondary-600 mt-2">管理團隊成員的角色、帳號和基本時薪設定</p>
      </div>

      {/* 篩選器 */}
      <UserFilters
        filters={filters}
        onFiltersChange={setFilters}
        onCreateUser={handleCreateUser}
      />

      {/* 用戶列表 */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="card animate-pulse">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                </div>
                <div className="flex space-x-2">
                  <div className="h-8 bg-gray-200 rounded flex-1"></div>
                  <div className="h-8 bg-gray-200 rounded flex-1"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-secondary-800 mb-2">
            {filters.search ? '找不到符合條件的成員' : '還沒有團隊成員'}
          </h3>
          <p className="text-secondary-600 mb-6">
            {filters.search 
              ? '請嘗試調整搜尋條件或篩選器'
              : '新增您的第一個團隊成員來開始管理'
            }
          </p>
          {!filters.search && (
            <button
              onClick={handleCreateUser}
              className="btn-primary"
            >
              新增第一個成員
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              onEdit={handleEditUser}
              onSetHourlyRate={handleSetHourlyRate}
              onSetLevel={handleSetLevel}
              onManageBankInfo={handleManageBankInfo}
              onResetPassword={handleResetPassword}
              onToggleStatus={handleToggleUserStatus}
            />
          ))}
        </div>
      )}

      {/* 用戶表單 Modal */}
      {showUserForm && (
        <UserForm
          user={editingUser}
          onSubmit={handleSubmitUser}
          onCancel={handleCancelForm}
          isLoading={formLoading}
        />
      )}

      {/* 時薪設定 Modal */}
      {showHourlyRateModal && selectedUser && (
        <HourlyRateModal
          user={selectedUser}
          onSubmit={handleSubmitHourlyRate}
          onCancel={handleCancelModal}
          isLoading={formLoading}
        />
      )}

      {/* 職等設定 Modal */}
      {showUserLevelModal && selectedUser && (
        <UserLevelModal
          user={selectedUser}
          onSubmit={handleSubmitLevel}
          onCancel={handleCancelModal}
          isLoading={formLoading}
        />
      )}

      {/* 銀行資訊 Modal */}
      {showBankInfoModal && selectedUser && (
        <BankInfoModal
          user={selectedUser}
          onSubmit={handleSubmitBankInfo}
          onCancel={handleCancelModal}
          isLoading={formLoading}
        />
      )}

      {/* 密碼重設 Modal */}
      {showPasswordResetModal && selectedUser && (
        <PasswordResetModal
          user={selectedUser}
          onSubmit={handleSubmitPasswordReset}
          onCancel={handleCancelModal}
          isLoading={formLoading}
        />
      )}
    </div>
  )
}

export default UsersPage
